import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createCopilotApi } from './api/copilotApi';
import { OrderOverview } from './components/OrderOverview';
import { CustomizationPanel } from './components/CustomizationPanel';
import { CopilotPanel } from './components/CopilotPanel';
import { OperationsRadar } from './components/OperationsRadar';
import { OperationsRadarBriefPanel } from './components/OperationsRadarBriefPanel';
import { normalizeOrderNo } from './utils/order';
import './styles.css';

export function AIOperationsWorkspace({ accessToken, apiBaseUrl = '' }) {
  const api = useMemo(
    () => createCopilotApi({ baseUrl: apiBaseUrl, accessToken }),
    [apiBaseUrl, accessToken]
  );

  const [auth, setAuth] = useState({ loading: true, user: null, error: null });
  const [workspaceMode, setWorkspaceMode] = useState('radar');

  const [orderInput, setOrderInput] = useState('');
  const [orderNo, setOrderNo] = useState('');
  const [orderContext, setOrderContext] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [customizationContext, setCustomizationContext] = useState(null);
  const [customizationLoading, setCustomizationLoading] = useState(false);
  const [customizationError, setCustomizationError] = useState(null);
  const [askBusy, setAskBusy] = useState(false);
  const customizationLoadedFor = useRef(null);

  const [radar, setRadar] = useState(null);
  const [radarLoading, setRadarLoading] = useState(false);
  const [radarError, setRadarError] = useState(null);
  const [radarAskBusy, setRadarAskBusy] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setAuth({ loading: true, user: null, error: null });
    api.me(controller.signal)
      .then((user) => setAuth({ loading: false, user, error: null }))
      .catch((error) => setAuth({ loading: false, user: null, error }));
    return () => controller.abort();
  }, [api]);

  useEffect(() => {
    if (
      !auth.loading &&
      auth.user?.csrAccess &&
      workspaceMode === 'radar' &&
      !radar &&
      !radarLoading
    ) {
      loadRadar();
    }
  }, [auth.loading, auth.user?.csrAccess, workspaceMode]);

  async function loadOrder(rawValue = orderInput) {
    const normalized = normalizeOrderNo(rawValue);
    if (!normalized) return;

    setWorkspaceMode('order');
    setOrderInput(normalized);
    setOrderNo(normalized);
    setOrderLoading(true);
    setOrderError(null);
    setOrderContext(null);
    setCustomizationContext(null);
    setCustomizationError(null);
    customizationLoadedFor.current = null;
    setActiveTab('overview');

    try {
      const context = await api.orderContext(normalized, 'order_status');
      setOrderContext(context);
    } catch (error) {
      setOrderError(error?.correlationId ? `${error.message} Correlation: ${error.correlationId}` : error.message);
    } finally {
      setOrderLoading(false);
    }
  }

  async function ensureCustomization() {
    if (!orderNo || customizationLoadedFor.current === orderNo) return;
    setCustomizationLoading(true);
    setCustomizationError(null);
    try {
      const context = await api.orderContext(orderNo, 'customization');
      setCustomizationContext(context);
      customizationLoadedFor.current = orderNo;
    } catch (error) {
      setCustomizationError(error.message);
    } finally {
      setCustomizationLoading(false);
    }
  }

  async function openCustomization() {
    setActiveTab('customization');
    await ensureCustomization();
  }

  async function ask(question) {
    setAskBusy(true);
    try {
      return await api.ask(orderNo, question);
    } finally {
      setAskBusy(false);
    }
  }

  async function loadRadar() {
    setRadarLoading(true);
    setRadarError(null);
    try {
      setRadar(await api.operationsRadar(500));
    } catch (error) {
      setRadarError(error?.correlationId ? `${error.message} Correlation: ${error.correlationId}` : error.message);
    } finally {
      setRadarLoading(false);
    }
  }

  async function openRadar() {
    setWorkspaceMode('radar');
    if (!radar) await loadRadar();
  }

  async function askRadar(question) {
    setRadarAskBusy(true);
    try {
      return await api.operationsRadarBrief(question, 500);
    } finally {
      setRadarAskBusy(false);
    }
  }

  async function submitPilotFeedback(correlationId, rating, category = null) {
    return await api.submitPilotFeedback(
      correlationId,
      rating,
      category
    );
  }

  if (auth.loading) {
    return <FullScreenState title="Connecting to Pulse CRM…" detail="Validating your existing CSR session." />;
  }

  if (auth.error || !auth.user?.csrAccess) {
    return (
      <FullScreenState
        title="CSR access required"
        detail="This workspace uses the existing Pulse CRM identity. Open it from an authenticated CSR session and pass the same bearer token used by CRM API calls."
        error
      />
    );
  }

  return (
    <div className="ai-workspace-shell">
      <header className="workspace-header workspace-header--with-mode">
        <div className="brand-and-mode">
          <div className="brand-block">
            <div className="brand-mark">AI</div>
            <div>
              <div className="brand-title-row">
                <div className="brand-title">Operations Copilot</div>
                {auth.user.pilotMode ? <span className="pilot-badge">Pilot</span> : null}
              </div>
              <div className="brand-subtitle">Enterprise Operations Intelligence</div>
            </div>
          </div>
          <div className="workspace-mode-switch" aria-label="Workspace mode">
            <button className={workspaceMode === 'radar' ? 'active' : ''} onClick={openRadar}>
              Operations radar
            </button>
            <button className={workspaceMode === 'order' ? 'active' : ''} onClick={() => setWorkspaceMode('order')}>
              Order workspace
            </button>
          </div>
        </div>

        {workspaceMode === 'order' ? (
          <form
            className="order-search"
            onSubmit={(event) => {
              event.preventDefault();
              loadOrder();
            }}
          >
            <label htmlFor="order-search-input">Order number</label>
            <div className="order-search__control">
              <input
                id="order-search-input"
                value={orderInput}
                onChange={(event) => setOrderInput(event.target.value)}
                placeholder="PU-3460"
                autoComplete="off"
                spellCheck="false"
              />
              <button className="primary-button" type="submit" disabled={orderLoading || !orderInput.trim()}>
                {orderLoading ? 'Loading…' : 'Open order'}
              </button>
            </div>
          </form>
        ) : (
          <div className="radar-header-copy">
            <span className="eyebrow">Senior operations view</span>
            <strong>Cross-order exceptions · read-only</strong>
          </div>
        )}

        <div className="user-chip">
          <span className="user-chip__avatar">{String(auth.user.username || '?').charAt(0).toUpperCase()}</span>
          <div>
            <strong>{auth.user.username}</strong>
            <span>CSR</span>
          </div>
        </div>
      </header>

      {auth.user.pilotMode ? (
        <div className="pilot-banner">
          <strong>Internal production pilot</strong>
          <span>Read-only operational intelligence. Validate critical actions against source systems during the pilot.</span>
        </div>
      ) : null}

      {workspaceMode === 'radar' ? (
        <main className="workspace-layout">
          <section className="workspace-main">
            <OperationsRadar
              data={radar}
              loading={radarLoading}
              error={radarError}
              onRefresh={loadRadar}
              onOpenOrder={loadOrder}
            />
          </section>
          <OperationsRadarBriefPanel
            onAsk={askRadar}
            onFeedback={submitPilotFeedback}
            busy={radarAskBusy}
            radarAvailable={Boolean(radar)}
            radarVersion={radar?.summary?.generatedAtUtc ?? null}
          />
        </main>
      ) : (
        <main className="workspace-layout">
          <section className="workspace-main">
            {!orderNo ? (
              <WelcomeState
                onExample={() => { setOrderInput('PU-3460'); loadOrder('PU-3460'); }}
                onRadar={openRadar}
              />
            ) : orderLoading ? (
              <div className="panel-loading large">Loading minimized order context…</div>
            ) : orderError ? (
              <div className="order-error">
                <h2>Could not open {orderNo}</h2>
                <p>{orderError}</p>
                <button className="secondary-button" onClick={() => loadOrder(orderNo)}>Try again</button>
              </div>
            ) : orderContext ? (
              <>
                <nav className="workspace-tabs" aria-label="Order workspace sections">
                  <button className={activeTab === 'overview' ? 'active' : ''} onClick={() => setActiveTab('overview')}>Overview</button>
                  <button
                    className={activeTab === 'customization' ? 'active' : ''}
                    onClick={openCustomization}
                  >
                    Customization
                    {orderContext.operationalSignals?.hasEmbroidery ? <span className="tab-dot" /> : null}
                  </button>
                </nav>

                {activeTab === 'overview' ? (
                  <OrderOverview context={orderContext} onOpenCustomization={openCustomization} />
                ) : (
                  <CustomizationPanel
                    context={customizationContext}
                    loading={customizationLoading}
                    error={customizationError}
                  />
                )}
              </>
            ) : null}
          </section>

          <CopilotPanel
            orderNo={orderContext ? orderNo : ''}
            orderContext={orderContext}
            onAsk={ask}
            onFeedback={submitPilotFeedback}
            busy={askBusy}
          />
        </main>
      )}
    </div>
  );
}

function FullScreenState({ title, detail, error = false }) {
  return (
    <div className={`full-screen-state ${error ? 'full-screen-state--error' : ''}`}>
      <div className="brand-mark large">AI</div>
      <h1>{title}</h1>
      <p>{detail}</p>
    </div>
  );
}

function WelcomeState({ onExample, onRadar }) {
  return (
    <div className="welcome-state">
      <div className="welcome-state__icon">✦</div>
      <div className="eyebrow">Operations intelligence</div>
      <h1>Grounded intelligence from one order to the operation.</h1>
      <p>Open an order for CSR investigation, or use Operations Radar to find active overdue exceptions across the business.</p>
      <div className="welcome-actions">
        <button className="secondary-button" onClick={onExample}>Open PU-3460</button>
        <button className="primary-button" onClick={onRadar}>Open Operations Radar</button>
      </div>
    </div>
  );
}
